import { Link } from 'react-router-dom';
import { NotebookPen, Plus } from 'lucide-react';
import { tr } from '@/lib/i18n-core';
import { useAuth } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { Button } from '@/components/ui/primitives';
import { ShellEmpty, ShellSectionHeading } from '@/components/clinical-shell/ShellPanel';
import { admissionIsOpen, ipdEmrPath, type Admission } from '../../admission/types';

export function ProgressNotesSection({ admission }: { admission: Admission }) {
  const { can } = useAuth();
  const current = admissionIsOpen(admission);
  const addTo = `/ipd/admissions/${admission.id}/notes/new?kind=progress&returnTo=${encodeURIComponent(ipdEmrPath(admission.id, 'progress-notes'))}`;

  return (
    <>
      <ShellSectionHeading
        icon={NotebookPen}
        title={tr('ipd', 'doctor_progress_notes')}
        description={tr('ipd', 'ws_progress_notes_desc')}
        actions={
          current && can('encounter.write', 'cpoe.write') ? (
            <Link to={addTo}>
              <Button size="sm">
                <Plus className="h-4 w-4" aria-hidden /> {tr('ipd', 'add_note')}
              </Button>
            </Link>
          ) : null
        }
      />
      {admission.progressNotes.length === 0 && <ShellEmpty>{tr('ipd', 'no_progress_notes_yet')}</ShellEmpty>}
      <div className="space-y-3">
        {admission.progressNotes.map((note) => (
          <article key={note.id} className="rounded-2xl border bg-card p-4 text-sm shadow-sm">
            <p className="mb-2 text-xs text-muted-foreground">{dateTime(note.createdAt)}</p>
            <dl className="grid gap-2 sm:grid-cols-2">
              {note.subjective && (
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{tr('ipd', 'subjective')}</dt>
                  <dd className="whitespace-pre-line">{note.subjective}</dd>
                </div>
              )}
              {note.objective && (
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{tr('ipd', 'objective')}</dt>
                  <dd className="whitespace-pre-line">{note.objective}</dd>
                </div>
              )}
              {note.assessment && (
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{tr('ipd', 'assessment')}</dt>
                  <dd className="whitespace-pre-line">{note.assessment}</dd>
                </div>
              )}
              {note.plan && (
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{tr('ipd', 'plan')}</dt>
                  <dd className="whitespace-pre-line">{note.plan}</dd>
                </div>
              )}
            </dl>
          </article>
        ))}
      </div>
    </>
  );
}
